import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { RotateCcw, Maximize2, Minimize2, ChevronDown, ChevronUp, Cloud, CloudOff } from 'lucide-react';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import EbookPageFooter from '../shared/EbookPageFooter';
import {
  FeelIcon, NeedIcon, ChapterIcon, SaveIcon,
  ArrowRightIcon, ArrowLeftIcon
} from '../icons/ElchmeIcons';

// Beautiful images for Chapter 1
const CHAPTER_IMAGES = {
  hero: 'https://customer-assets.emergentagent.com/job_nvc-wisdom/artifacts/3e6w0yfm_869447EF-490D-4C3F-8304-A516675595C1.jpeg',
  camp: 'https://customer-assets.emergentagent.com/job_nvc-wisdom/artifacts/5wjs98t3_E1FD6FD5-FC07-4ABD-950E-473041A744EC.jpeg',
  meadow: 'https://customer-assets.emergentagent.com/job_nvc-wisdom/artifacts/15bgt906_IMG_3564.jpeg',
  barn: 'https://customer-assets.emergentagent.com/job_nvc-wisdom/artifacts/q5a5cnlo_IMG_2771.jpeg',
  joyful: 'https://customer-assets.emergentagent.com/job_nvc-wisdom/artifacts/ojj6hb2v_IMG_1188.jpeg',
  treeOfLife: 'https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/62rxcjrj_TREE%20LIFE.jpg',
};

export default function Chapter1Presentation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = parseInt(searchParams.get('section')) || 0;
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isHelperOpen, setIsHelperOpen] = useState(false); // Feelings & Needs Helper panel
  
  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  // Exercise responses state - load from localStorage first, then sync from server
  const [responses, setResponses] = useState(() => {
    const saved = localStorage.getItem('chapter1Responses');
    return saved ? JSON.parse(saved) : {};
  });

  // Four Basic Choices responses
  const [fourChoicesResponses, setFourChoicesResponses] = useState(() => {
    const saved = localStorage.getItem('chapter1FourChoices');
    return saved ? JSON.parse(saved) : {};
  });

  // Four D's Matching Exercise State
  const [matchingAnswers, setMatchingAnswers] = useState({});
  const [showMatchingAnswers, setShowMatchingAnswers] = useState(false);
  const [matchingComplete, setMatchingComplete] = useState(false);
  
  // Exercise 2 State - Multi-select patterns
  const [exercise2Selections, setExercise2Selections] = useState({});
  const [showExercise2Answers, setShowExercise2Answers] = useState(false);
  
  // Exercise 3 State - Multi-select patterns
  const [exercise3Selections, setExercise3Selections] = useState({});
  const [showExercise3AnswersNew, setShowExercise3AnswersNew] = useState(false);
  
  // Exercise 3 State - Identify the D
  const [exercise3Answers, setExercise3Answers] = useState({});
  const [showExercise3Answers, setShowExercise3Answers] = useState(false);

  // Exercise 2 Data with correct answers (can be multiple)
  const exercise2Data = [
    { id: 1, text: "I've told you hundreds of times, I can't read your mind.", answers: [1, 11], answerNames: "Defending, Blaming" },
    { id: 2, text: "You know, you're absolutely right. I'm sorry. I'll try to do better.", answers: [2], answerNames: "Apologizing" },
    { id: 3, text: "You could try writing me a note that I can look at just before I leave work.", answers: [3], answerNames: "Advising" },
    { id: 4, text: "Yeah, right, like you care.", answers: [4], answerNames: "Sarcasm" },
    { id: 5, text: "It reminds me of the time I was telling my mom that I'd be home at such and such a time and she...", answers: [5], answerNames: "Storytelling" },
    { id: 6, text: "The reason I came home late was because I was held up at work.", answers: [6], answerNames: "Explaining" },
    { id: 7, text: "Oh, there, there. It'll be all right.", answers: [7], answerNames: "Consoling" },
    { id: 8, text: "The way I see it, you're reacting out of proportion to the situation.", answers: [8], answerNames: "Reframing" },
    { id: 9, text: "When does this become a problem?", answers: [9], answerNames: "Information Request" },
    { id: 10, text: "That's absolutely not true!", answers: [10], answerNames: "Negating" },
    { id: 11, text: "There wouldn't be a problem if you weren't so sensitive.", answers: [11], answerNames: "Blaming" },
    { id: 12, text: "I know, I'm a complete failure.", answers: [12], answerNames: "Self Reproach" },
    { id: 13, text: "Yeah, you're right, I should.", answers: [13], answerNames: "Agreeing" },
    { id: 14, text: "I'll just call next time.", answers: [14], answerNames: "Fixing" }
  ];
  
  // Exercise 3 Data - Dialogues with correct answers (can be multiple)
  const exercise3DialogueData = [
    { id: 1, a: "You shouldn't have done that.", b: "I know. I'm so inconsiderate.", answers: ["selfReproach"], answerNames: "Self Reproach" },
    { id: 2, a: "My husband is so mean to me. I just can't believe all of the things that he has done.", b: "It's ok. I'm here now. He won't say anything nasty now.", answers: ["consoling"], answerNames: "Consoling" },
    { id: 3, a: "Why can't you just be more like your sister? She would have done the right thing.", b: "I'm sorry. It won't happen again.", answers: ["apologizing"], answerNames: "Apologizing" },
    { id: 4, a: "I hope that you will do a better job next time.", b: "Why do you always make it about you?", answers: ["blaming"], answerNames: "Blaming" },
    { id: 5, a: "You're so lazy!", b: "You're the one that's lazy. You don't do anything around here.", answers: ["defending", "blaming"], answerNames: "Defending, Blaming" },
    { id: 6, a: "You never listen to me!", b: "I do listen, you just don't pay attention.", answers: ["denying", "defending"], answerNames: "Denying, Defending" },
    { id: 7, a: "Why didn't you wash the dishes like I asked you?", b: "I was going to but then my friend called and I got distracted.", answers: ["explaining"], answerNames: "Explaining" },
    { id: 8, a: "Tell me when you're coming home.", b: "You'll know I'm home when I get back here.", answers: ["sarcasm"], answerNames: "Sarcasm" },
    { id: 9, a: "My friends are making me so mad.", b: "Have you tried therapy?", answers: ["advising"], answerNames: "Advising" },
    { id: 10, a: "When my wife looks at me like that I get so frustrated.", b: "Don't worry about it.", answers: ["reframing", "consoling"], answerNames: "Reframing, Consoling" }
  ];

  // Pattern options for multi-select
  const patternOptions = [
    { id: 1, name: "Defending" },
    { id: 2, name: "Apologizing" },
    { id: 3, name: "Advising" },
    { id: 4, name: "Sarcasm" },
    { id: 5, name: "Storytelling" },
    { id: 6, name: "Explaining" },
    { id: 7, name: "Consoling" },
    { id: 8, name: "Reframing" },
    { id: 9, name: "Information Request" },
    { id: 10, name: "Negating" },
    { id: 11, name: "Blaming" },
    { id: 12, name: "Self Reproach" },
    { id: 13, name: "Agreeing" },
    { id: 14, name: "Fixing" }
  ];

  // Exercise 3 pattern options
  const exercise3PatternOptions = [
    { id: "advising", name: "Advising" },
    { id: "agreeing", name: "Agreeing" },
    { id: "apologizing", name: "Apologizing" },
    { id: "blaming", name: "Blaming" },
    { id: "consoling", name: "Consoling" },
    { id: "defending", name: "Defending" },
    { id: "denying", name: "Denying" },
    { id: "explaining", name: "Explaining" },
    { id: "negating", name: "Negating" },
    { id: "reframing", name: "Reframing" },
    { id: "requestingInfo", name: "Requesting Information" },
    { id: "sarcasm", name: "Sarcasm" },
    { id: "selfReproach", name: "Self Reproach" },
    { id: "storytelling", name: "Story-telling" },
    { id: "fixing", name: "Fixing" }
  ];

  // Toggle selection for Exercise 2
  const toggleExercise2Selection = (questionId, patternId) => {
    setExercise2Selections(prev => {
      const current = prev[questionId] || [];
      if (current.includes(patternId)) {
        return { ...prev, [questionId]: current.filter(id => id !== patternId) };
      } else {
        return { ...prev, [questionId]: [...current, patternId] };
      }
    });
  };

  // Toggle selection for Exercise 3
  const toggleExercise3Selection = (questionId, patternId) => {
    setExercise3Selections(prev => {
      const current = prev[questionId] || [];
      if (current.includes(patternId)) {
        return { ...prev, [questionId]: current.filter(id => id !== patternId) };
      } else {
        return { ...prev, [questionId]: [...current, patternId] };
      }
    });
  };

  // Fetch user's saved progress from server
  const fetchUserProgress = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.responses) {
          setResponses(data.responses);
          localStorage.setItem('chapter1Responses', JSON.stringify(data.responses));
        }
        if (data.fourChoicesResponses) {
          setFourChoicesResponses(data.fourChoicesResponses);
          localStorage.setItem('chapter1FourChoices', JSON.stringify(data.fourChoicesResponses));
        }
        setLastSyncTime(new Date());
      }
    } catch (error) {
      console.log('Could not fetch progress from server, using local data');
    }
  }, []);

  // Load from server on mount if logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchUserProgress();
    }
  }, [isLoggedIn, fetchUserProgress]);

  // Save progress to server
  const saveToServer = useCallback(async (responsesData, fourChoicesData) => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    setIsSyncing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter1`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          responses: responsesData,
          fourChoicesResponses: fourChoicesData
        })
      });
      if (response.ok) {
        setLastSyncTime(new Date());
        return true;
      }
    } catch (error) {
      console.error('Failed to save to server:', error);
    } finally {
      setIsSyncing(false);
    }
    return false;
  }, []);

  // Auto-save to localStorage and server
  useEffect(() => {
    const interval = setInterval(() => {
      localStorage.setItem('chapter1Responses', JSON.stringify(responses));
      localStorage.setItem('chapter1FourChoices', JSON.stringify(fourChoicesResponses));
      // Also save to server if logged in
      if (isLoggedIn) {
        saveToServer(responses, fourChoicesResponses);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [responses, fourChoicesResponses, isLoggedIn, saveToServer]);

  const saveProgress = async () => {
    localStorage.setItem('chapter1Responses', JSON.stringify(responses));
    localStorage.setItem('chapter1FourChoices', JSON.stringify(fourChoicesResponses));
    
    if (isLoggedIn) {
      const success = await saveToServer(responses, fourChoicesResponses);
      if (success) {
        toast.success('Progress saved to your account!');
      } else {
        toast.success('Saved locally (server sync failed)');
      }
    } else {
      toast.success('Saved locally! Sign in to sync across devices.');
    }
  };

  const resetAll = () => {
    if (window.confirm('Reset all Chapter 1 responses? This cannot be undone.')) {
      setResponses({});
      setFourChoicesResponses({});
      localStorage.removeItem('chapter1Responses');
      localStorage.removeItem('chapter1FourChoices');
      if (isLoggedIn) {
        saveToServer({}, {});
      }
      toast.success('All responses reset!');
    }
  };

  // Communication Options Scenarios (Page 79-80)
  const communicationScenarios = [
    { id: 1, statement: "You're always bossing me around." },
    { id: 2, statement: "You never listen to me." },
    { id: 3, statement: "Why do I always have to do everything around here?" },
    { id: 4, statement: "My job is such a pain in the neck." },
    { id: 5, statement: "Why do you text me so much?" },
    { id: 6, statement: "Leave me alone; I'll figure it out myself." },
    { id: 7, statement: "You're so lazy." },
    { id: 8, statement: "Why do you talk so much?" },
    { id: 9, statement: "You don't deserve my help." },
    { id: 10, statement: "You're such a narcissist." },
  ];

  // Four Basic Choices Scenarios (Pages 82-86)
  const fourChoicesScenarios = [
    { id: 1, statement: "Mom always favors you." },
    { id: 2, statement: "Your life is such a mess." },
    { id: 3, statement: "You are so self-centered." },
    { id: 4, statement: "I can't count on you for anything." },
    { id: 5, statement: "Stop bragging so much." },
    { id: 6, statement: "Why do you always talk to your friends about our relationship?" },
    { id: 7, statement: "Do you care about anything besides money?" },
    { id: 8, statement: "You're going on vacation again without me?" },
    { id: 9, statement: "You're such a slob; you always make a mess." },
    { id: 10, statement: "Stop whining; grow up." },
    { id: 11, statement: "Can't you just listen to me for once?" },
    { id: 12, statement: "You don't understand anything about me, do you." },
    { id: 13, statement: "You're always bossing me around." },
    { id: 14, statement: "You always dominate our conversations." },
    { id: 15, statement: "Why do I always have to do everything around here?" },
    { id: 16, statement: "You always make my job so hard." },
    { id: 17, statement: "Don't be such a wimp. Stand up for yourself." },
    { id: 18, statement: "Why do you text me so much?" },
    { id: 19, statement: "All you ever do is complain." },
    { id: 20, statement: "Talking to you is like talking to a wall." },
    { id: 21, statement: "If everybody did as little as you, this company would go out of business." },
    { id: 22, statement: "With how little you try, you expect to pass this class?" },
    { id: 23, statement: "How come I only hear from you when you need something?" },
    { id: 24, statement: "Stop hovering! Give me some space." },
  ];

  // Custom blank scenarios for user's own statements
  const customScenarios = [
    { id: 'custom1', statement: '' },
    { id: 'custom2', statement: '' },
    { id: 'custom3', statement: '' },
    { id: 'custom4', statement: '' },
  ];

  // The Four D's of Disconnection
  const fourDs = [
    {
      name: "Diagnosis",
      meaning: "Analyzing, comparing, labeling, interpreting",
      example: "You're too sensitive",
      form: "Judgments disguised as observations"
    },
    {
      name: "Denial of Responsibility",
      meaning: "Attributing our actions to external forces",
      example: "I had to do it because my boss said so",
      form: "\"I had to...\", \"You made me...\""
    },
    {
      name: "Demand",
      meaning: "Implying threat of blame or punishment",
      example: "You better clean your room right now",
      form: "\"You should...\", \"You have to...\""
    },
    {
      name: "Deserve",
      meaning: "Thinking people deserve punishment or reward",
      example: "They got what was coming to them",
      form: "\"They deserve...\", \"Serves them right\""
    }
  ];

  // Sections for presentation navigation
  const sections = [
    { id: 'intro', title: 'Chapter Introduction', page: 79 },
    { id: 'example', title: 'Example Response', page: 79 },
    { id: 'exercise1', title: 'Communication Options Exercise', page: 79 },
    { id: 'four-choices', title: 'Four Basic Choices of Response', page: 81 },
    { id: 'four-choices-exercise', title: 'Four Choices Exercise', page: 82 },
    { id: 'jackal-ofnr', title: 'Jackal OFNR Practice', page: 87 },
    { id: 'street-giraffe', title: 'Street Giraffe Practice', page: 88 },
    { id: 'using-ofnr', title: 'Using OFNR for Connection', page: 89 },
    { id: 'four-ds', title: "The Four D's of Disconnection", page: 90 },
    { id: 'homework', title: 'Chapter 1 Homework', page: 93 },
  ];

  const progress = ((currentSection + 1) / sections.length) * 100;

  const togglePresentationMode = async () => {
    const newMode = !isPresentationMode;
    setIsPresentationMode(newMode);
    
    if (newMode) {
      // Entering presentation mode - try fullscreen
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          await document.documentElement.webkitRequestFullscreen();
        }
      } catch (error) {
        // Fullscreen may fail due to browser restrictions, but presentation mode still works
        console.log('Fullscreen not available, using presentation mode without fullscreen');
        toast.info('Presentation mode active! (Fullscreen not available in this browser)');
      }
    } else {
      // Exiting presentation mode
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else if (document.webkitFullscreenElement) {
          await document.webkitExitFullscreen();
        }
      } catch (error) {
        // Exit fullscreen may fail, ignore
        console.log('Could not exit fullscreen');
      }
    }
  };

  // Keyboard navigation for presentation mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isPresentationMode) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          setCurrentSection(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
          setIsPresentationMode(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPresentationMode, sections.length]);

  const updateResponse = (scenarioId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [scenarioId]: { ...prev[scenarioId], [field]: value }
    }));
  };

  const updateFourChoicesResponse = (scenarioId, choice, value) => {
    setFourChoicesResponses(prev => ({
      ...prev,
      [scenarioId]: { ...prev[scenarioId], [choice]: value }
    }));
  };

  // Presentation wrapper
  const presentationClass = isPresentationMode 
    ? "fixed inset-0 z-50 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-auto" 
    : "min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900";

  return (
    <div className={presentationClass} data-testid="chapter1-presentation">
      {/* Header */}
      <div className={`border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 ${isPresentationMode ? 'px-8' : ''}`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {!isPresentationMode && (
                <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                  <ArrowLeftIcon size={16} className="mr-2" />
                  Back to Guide
                </Button>
              )}
              <div>
                <h1 className={`font-bold text-white ${isPresentationMode ? 'text-3xl' : 'text-xl lg:text-2xl'}`}>
                  Chapter 1: Communication that Blocks Compassion
                </h1>
                <p className="text-slate-400 text-sm">Page {sections[currentSection].page} • {sections[currentSection].title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Sync Status Indicator */}
              {isLoggedIn && (
                <div className="flex items-center gap-1 text-xs text-slate-400 mr-2">
                  {isSyncing ? (
                    <>
                      <Cloud className="h-3 w-3 animate-pulse text-blue-400" />
                      <span>Syncing...</span>
                    </>
                  ) : lastSyncTime ? (
                    <>
                      <Cloud className="h-3 w-3 text-green-400" />
                      <span>Synced</span>
                    </>
                  ) : (
                    <>
                      <CloudOff className="h-3 w-3 text-slate-500" />
                      <span>Not synced</span>
                    </>
                  )}
                </div>
              )}
              {!isLoggedIn && (
                <div className="flex items-center gap-1 text-xs text-amber-400 mr-2">
                  <CloudOff className="h-3 w-3" />
                  <span>Local only</span>
                </div>
              )}
              <Button onClick={saveProgress} variant="outline" size="sm" className="border-green-600 hover:border-green-500 text-white hover:bg-green-900/20">
                <SaveIcon size={16} className="mr-2" />
                Save
              </Button>
              <Button onClick={resetAll} variant="outline" size="sm" className="border-red-600 hover:border-red-500 text-white hover:bg-red-900/20">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              <Button 
                onClick={togglePresentationMode} 
                variant="outline" 
                size="sm" 
                className="border-purple-600 hover:border-purple-500 text-white hover:bg-purple-900/20"
                data-testid="presentation-mode-btn"
              >
                {isPresentationMode ? <Minimize2 className="h-4 w-4 mr-2" /> : <Maximize2 className="h-4 w-4 mr-2" />}
                {isPresentationMode ? 'Exit Presentation' : 'Presentation Mode'}
              </Button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={progress} className="h-2 bg-slate-700" />
            <div className="flex justify-between mt-2">
              {sections.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    idx === currentSection 
                      ? 'bg-teal-500 scale-125' 
                      : idx < currentSection 
                        ? 'bg-teal-700' 
                        : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                  title={section.title}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`container mx-auto ${isPresentationMode ? 'px-12 py-8' : 'px-6 py-8'}`}>
        <div className={`max-w-6xl mx-auto ${isPresentationMode ? 'text-lg' : ''}`}>

          {/* SECTION: Introduction */}
          {currentSection === 0 && (
            <div className="space-y-8 animate-fadeIn">
              {/* Hero Image */}
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={CHAPTER_IMAGES.hero} 
                  alt="Community gathering at sunset" 
                  className="w-full h-64 lg:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end">
                  <div className="p-8">
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">Communication that Blocks Compassion</h2>
                    <p className="text-xl text-slate-200">Chapter 1 • Understanding Our Response Patterns</p>
                  </div>
                </div>
              </div>

              {/* Introduction Text */}
              <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Communication Options to Increase Compassion</h3>
                <p className="text-slate-200 text-lg leading-relaxed mb-6">
                  In this chapter, we explore the difference between <strong className="text-teal-400">typical responses</strong> and <strong className="text-green-400">compassionate responses</strong>. 
                  We'll learn to recognize patterns that block connection and discover alternatives that create understanding.
                </p>
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                    <h4 className="text-blue-300 font-semibold mb-2">Typical Response</h4>
                    <p className="text-slate-300 text-sm">Negating, judging, blaming, arguing - reactions that often escalate conflict</p>
                  </div>
                  <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4">
                    <h4 className="text-green-300 font-semibold mb-2">Empathetic Response</h4>
                    <p className="text-slate-300 text-sm">Guessing the speaker's feelings and needs to create understanding</p>
                  </div>
                  <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4">
                    <h4 className="text-purple-300 font-semibold mb-2">Honesty Response</h4>
                    <p className="text-slate-300 text-sm">Expressing your own observations, feelings, needs, and requests (OFNR)</p>
                  </div>
                </div>
              </div>

              {/* Side-by-side images */}
              <div className="grid md:grid-cols-2 gap-6">
                <img src={CHAPTER_IMAGES.barn} alt="Gathering space" className="rounded-xl shadow-lg w-full h-48 object-cover" />
                <img src={CHAPTER_IMAGES.meadow} alt="Nature setting" className="rounded-xl shadow-lg w-full h-48 object-cover" />
              </div>
            </div>
          )}

          {/* SECTION: Example */}
          {currentSection === 1 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-amber-200 mb-6">Example Response Pattern</h3>
                
                <div className="bg-slate-800/60 rounded-xl p-6 mb-6">
                  <p className="text-2xl text-white font-semibold text-center mb-4">"You're so selfish."</p>
                </div>

                <div className="space-y-6">
                  <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-5">
                    <h4 className="text-red-300 font-bold text-lg mb-2">Typical Response:</h4>
                    <p className="text-slate-200 text-lg italic">"You're the one that's selfish..."</p>
                    <p className="text-red-300 text-sm mt-2">(negating, judging, blaming, arguing)</p>
                  </div>

                  <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-5">
                    <h4 className="text-green-300 font-bold text-lg mb-2">Empathetic Response:</h4>
                    <p className="text-slate-200 text-lg italic">"Are you feeling hurt because you are needing consideration?"</p>
                    <p className="text-green-300 text-sm mt-2">(guessing their feelings and needs)</p>
                  </div>

                  <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-5">
                    <h4 className="text-purple-300 font-bold text-lg mb-2">Honesty Response:</h4>
                    <p className="text-slate-200 text-lg italic">"When I hear that, I feel shocked because I have a need for appreciation of my efforts to help others. Would you be willing to tell me what you heard me say?"</p>
                    <p className="text-purple-300 text-sm mt-2">(expressing OFNR)</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 text-center">
                <p className="text-slate-300 text-lg">
                  Use your <span className="text-teal-400 font-semibold">feelings list</span> (page 5) and <span className="text-teal-400 font-semibold">needs list</span> (page 6) to help create responses.
                </p>
                <Button 
                  onClick={() => navigate('/ofnr-trainer')} 
                  variant="outline" 
                  className="mt-4 border-teal-600 text-white hover:bg-teal-900/20"
                >
                  Open Feelings & Needs Reference →
                </Button>
              </div>
            </div>
          )}

          {/* SECTION: Exercise 1 - Communication Options */}
          {currentSection === 2 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6 mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-200 mb-2">Communication Options Exercise</h3>
                    <p className="text-blue-100">
                      Imagine someone says these statements to you. Write a response for each type.
                    </p>
                  </div>
                  <Button
                    onClick={() => setIsHelperOpen(true)}
                    className="bg-gradient-to-r from-rose-600 to-emerald-600 hover:from-rose-500 hover:to-emerald-500 text-white flex items-center gap-2 whitespace-nowrap"
                  >
                    <FeelIcon size={16} />
                    <NeedIcon size={16} />
                    Feelings & Needs
                  </Button>
                </div>
              </div>

              <p className="text-center text-slate-400 text-sm">💾 Auto-saves every 30 seconds • Click expand to write responses</p>

              <div className="space-y-4">
                {communicationScenarios.map((scenario) => (
                  <div key={scenario.id} className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedExercise(expandedExercise === scenario.id ? null : scenario.id)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/40 transition-colors"
                    >
                      <h4 className="text-lg font-semibold text-white text-left">
                        {scenario.id}. "{scenario.statement}"
                      </h4>
                      {expandedExercise === scenario.id ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    
                    {expandedExercise === scenario.id && (
                      <div className="p-6 border-t border-slate-700/30 space-y-4">
                        <div>
                          <label className="text-sm font-medium text-red-300 mb-2 block">Typical response:</label>
                          <Textarea
                            value={responses[scenario.id]?.typical || ''}
                            onChange={(e) => updateResponse(scenario.id, 'typical', e.target.value)}
                            placeholder="Your typical response..."
                            className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 focus:border-red-600"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-green-300 mb-2 block">Empathetic response (guess their feelings & needs):</label>
                          <Textarea
                            value={responses[scenario.id]?.empathetic || ''}
                            onChange={(e) => updateResponse(scenario.id, 'empathetic', e.target.value)}
                            placeholder="Are you feeling... because you need...?"
                            className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 focus:border-green-600"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-purple-300 mb-2 block">Honesty response (express your OFNR):</label>
                          <Textarea
                            value={responses[scenario.id]?.honesty || ''}
                            onChange={(e) => updateResponse(scenario.id, 'honesty', e.target.value)}
                            placeholder="When I hear that, I feel... because I need... Would you be willing to...?"
                            className="bg-purple-900/20 border-purple-700/30 text-white placeholder:text-purple-300/40 focus:border-purple-600"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION: Four Basic Choices Visual */}
          {currentSection === 3 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Our 4 Basic Choices of Response</h2>
                <p className="text-slate-300 text-lg">In any given situation, we have four ways to respond...</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Choice 1 - Blame Back */}
                <div className="bg-gradient-to-br from-red-900/30 to-red-800/20 border border-red-700/40 rounded-xl p-6 relative overflow-hidden">
                  <div className="mb-4 rounded-lg overflow-hidden h-32">
                    <img 
                      src="https://images.unsplash.com/photo-1758524944737-fb8a4034541b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjb25mbGljdCUyMGFyZ3VtZW50fGVufDB8fHx8MTc2NDc1MTQwM3ww&ixlib=rb-4.1.0&q=85&w=400" 
                      alt="Conflict - blaming back" 
                      className="w-full h-full object-cover opacity-80"
                    />
                  </div>
                  <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">Choice #1</span>
                  <h3 className="text-xl font-bold text-red-300 mt-4 mb-2">Hear Blame, Blame Back</h3>
                  <p className="text-slate-300">
                    Defending ourselves by attacking the other person. This escalates conflict and creates distance.
                  </p>
                  <div className="mt-4 bg-red-900/30 rounded-lg p-3">
                    <p className="text-red-200 text-sm italic">"You think I'm selfish? Look who's talking!"</p>
                  </div>
                </div>

                {/* Choice 2 - Blame Self */}
                <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-700/40 rounded-xl p-6 relative overflow-hidden">
                  <div className="mb-4 rounded-lg overflow-hidden h-32">
                    <img 
                      src="https://images.unsplash.com/photo-1683505192959-9607e4b7f8ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxzYWQlMjBkZWplY3RlZHxlbnwwfHx8fDE3NjQ3NTE0MDl8MA&ixlib=rb-4.1.0&q=85&w=400" 
                      alt="Self-blame - dejected" 
                      className="w-full h-full object-cover opacity-80"
                    />
                  </div>
                  <span className="bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full">Choice #2</span>
                  <h3 className="text-xl font-bold text-amber-300 mt-4 mb-2">Hear Blame, Blame Self</h3>
                  <p className="text-slate-300">
                    Taking on guilt and shame. This harms our self-worth and doesn't address the real issue.
                  </p>
                  <div className="mt-4 bg-amber-900/30 rounded-lg p-3">
                    <p className="text-amber-200 text-sm italic">"You're right, I'm such a terrible person..."</p>
                  </div>
                </div>

                {/* Choice 3 - Own Feelings & Needs */}
                <div className="bg-gradient-to-br from-teal-900/30 to-teal-800/20 border border-teal-700/40 rounded-xl p-6 relative overflow-hidden">
                  <div className="mb-4 rounded-lg overflow-hidden h-32">
                    <img 
                      src="https://images.unsplash.com/photo-1505260540486-10233d451528?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxzZWxmJTIwcmVmbGVjdGlvbnxlbnwwfHx8fDE3NjQ3NTE0MTR8MA&ixlib=rb-4.1.0&q=85&w=400" 
                      alt="Self-reflection - connecting with own feelings" 
                      className="w-full h-full object-cover opacity-80"
                    />
                  </div>
                  <span className="bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full">Choice #3</span>
                  <h3 className="text-xl font-bold text-teal-300 mt-4 mb-2">Hear My Own Feelings & Needs</h3>
                  <p className="text-slate-300">
                    Connecting with what's alive in us. This is self-empathy and creates space for honest expression.
                  </p>
                  <div className="mt-4 bg-teal-900/30 rounded-lg p-3">
                    <p className="text-teal-200 text-sm italic">"I notice I feel hurt hearing this, because I value appreciation..."</p>
                  </div>
                </div>

                {/* Choice 4 - Speaker's Feelings & Needs */}
                <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/40 rounded-xl p-6 relative overflow-hidden">
                  <div className="mb-4 rounded-lg overflow-hidden h-32">
                    <img 
                      src="https://images.unsplash.com/photo-1764449319766-88a7942935e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxlbXBhdGh5JTIwY29ubmVjdGlvbnxlbnwwfHx8fDE3NjQ3NTE0MjB8MA&ixlib=rb-4.1.0&q=85&w=400" 
                      alt="Empathy connection - reaching out with curiosity" 
                      className="w-full h-full object-cover opacity-80"
                    />
                  </div>
                  <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">Choice #4</span>
                  <h3 className="text-xl font-bold text-green-300 mt-4 mb-2">Hear Speaker's Feelings & Needs</h3>
                  <p className="text-slate-300">
                    Empathizing with what the other person is experiencing. This creates connection and understanding.
                  </p>
                  <div className="mt-4 bg-green-900/30 rounded-lg p-3">
                    <p className="text-green-200 text-sm italic">"Are you feeling frustrated because you need more consideration?"</p>
                  </div>
                </div>
              </div>

              {/* Tree of Life Image */}
              <div className="flex justify-center mt-8">
                <img 
                  src={CHAPTER_IMAGES.treeOfLife} 
                  alt="Tree of Life" 
                  className="max-h-64 rounded-xl shadow-xl"
                />
              </div>
            </div>
          )}

          {/* SECTION: Four Choices Exercise */}
          {currentSection === 4 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="bg-teal-900/20 border border-teal-700/30 rounded-xl p-6 mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-teal-200 mb-2">Four Basic Choices Exercise</h3>
                    <p className="text-teal-100">
                      Practice writing all four types of responses for each statement. (24 scenarios + 4 custom)
                    </p>
                  </div>
                  <Button
                    onClick={() => setIsHelperOpen(true)}
                    className="bg-gradient-to-r from-rose-600 to-emerald-600 hover:from-rose-500 hover:to-emerald-500 text-white flex items-center gap-2 whitespace-nowrap"
                  >
                    <FeelIcon size={16} />
                    <NeedIcon size={16} />
                    Feelings & Needs
                  </Button>
                </div>
              </div>

              <p className="text-center text-slate-400 text-sm">💾 Auto-saves every 30 seconds • Click expand to write responses</p>

              {/* All 24 Scenarios */}
              <div className="space-y-4">
                {fourChoicesScenarios.map((scenario) => (
                  <div key={scenario.id} className="bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedExercise(`fc-${scenario.id}` === expandedExercise ? null : `fc-${scenario.id}`)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/40 transition-colors"
                    >
                      <h4 className="text-lg font-semibold text-white text-left">
                        {scenario.id}. "{scenario.statement}"
                      </h4>
                      {expandedExercise === `fc-${scenario.id}` ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    
                    {expandedExercise === `fc-${scenario.id}` && (
                      <div className="p-6 border-t border-slate-700/30 grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-red-300 mb-2 block">1. Blame Back:</label>
                          <Textarea
                            value={fourChoicesResponses[scenario.id]?.blameBack || ''}
                            onChange={(e) => updateFourChoicesResponse(scenario.id, 'blameBack', e.target.value)}
                            placeholder="Defensive response..."
                            className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 focus:border-red-600 h-24"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-amber-300 mb-2 block">2. Blame Self:</label>
                          <Textarea
                            value={fourChoicesResponses[scenario.id]?.blameSelf || ''}
                            onChange={(e) => updateFourChoicesResponse(scenario.id, 'blameSelf', e.target.value)}
                            placeholder="Taking on blame..."
                            className="bg-amber-900/20 border-amber-700/30 text-white placeholder:text-amber-300/40 focus:border-amber-600 h-24"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-teal-300 mb-2 block">3. My Feelings & Needs:</label>
                          <Textarea
                            value={fourChoicesResponses[scenario.id]?.myFeelings || ''}
                            onChange={(e) => updateFourChoicesResponse(scenario.id, 'myFeelings', e.target.value)}
                            placeholder="I feel... because I need..."
                            className="bg-teal-900/20 border-teal-700/30 text-white placeholder:text-teal-300/40 focus:border-teal-600 h-24"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-green-300 mb-2 block">4. Speaker's Feelings & Needs:</label>
                          <Textarea
                            value={fourChoicesResponses[scenario.id]?.speakerFeelings || ''}
                            onChange={(e) => updateFourChoicesResponse(scenario.id, 'speakerFeelings', e.target.value)}
                            placeholder="Are you feeling... because you need...?"
                            className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 focus:border-green-600 h-24"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Custom Blank Scenarios Section */}
              <div className="mt-8 pt-8 border-t border-slate-600/50">
                <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-6 mb-6">
                  <h4 className="text-xl font-bold text-purple-200 mb-2">Your Own Statements</h4>
                  <p className="text-purple-100 text-sm">
                    Use these blank options to develop responses to statements people have actually said to you.
                  </p>
                </div>

                <div className="space-y-4">
                  {customScenarios.map((scenario, idx) => (
                    <div key={scenario.id} className="bg-purple-900/20 border border-purple-700/30 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedExercise(`fc-${scenario.id}` === expandedExercise ? null : `fc-${scenario.id}`)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-purple-800/20 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className="bg-purple-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </span>
                          <input
                            type="text"
                            value={fourChoicesResponses[scenario.id]?.customStatement || ''}
                            onChange={(e) => {
                              e.stopPropagation();
                              updateFourChoicesResponse(scenario.id, 'customStatement', e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Enter a statement someone has said to you..."
                            className="flex-1 bg-transparent border-b border-purple-500/30 text-white placeholder:text-purple-300/40 focus:outline-none focus:border-purple-400 py-1"
                          />
                        </div>
                        {expandedExercise === `fc-${scenario.id}` ? (
                          <ChevronUp className="h-5 w-5 text-purple-400 ml-4" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-purple-400 ml-4" />
                        )}
                      </button>
                      
                      {expandedExercise === `fc-${scenario.id}` && (
                        <div className="p-6 border-t border-purple-700/30 grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-red-300 mb-2 block">1. Blame Back:</label>
                            <Textarea
                              value={fourChoicesResponses[scenario.id]?.blameBack || ''}
                              onChange={(e) => updateFourChoicesResponse(scenario.id, 'blameBack', e.target.value)}
                              placeholder="Defensive response..."
                              className="bg-red-900/20 border-red-700/30 text-white placeholder:text-red-300/40 focus:border-red-600 h-24"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-amber-300 mb-2 block">2. Blame Self:</label>
                            <Textarea
                              value={fourChoicesResponses[scenario.id]?.blameSelf || ''}
                              onChange={(e) => updateFourChoicesResponse(scenario.id, 'blameSelf', e.target.value)}
                              placeholder="Taking on blame..."
                              className="bg-amber-900/20 border-amber-700/30 text-white placeholder:text-amber-300/40 focus:border-amber-600 h-24"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-teal-300 mb-2 block">3. My Feelings & Needs:</label>
                            <Textarea
                              value={fourChoicesResponses[scenario.id]?.myFeelings || ''}
                              onChange={(e) => updateFourChoicesResponse(scenario.id, 'myFeelings', e.target.value)}
                              placeholder="I feel... because I need..."
                              className="bg-teal-900/20 border-teal-700/30 text-white placeholder:text-teal-300/40 focus:border-teal-600 h-24"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-green-300 mb-2 block">4. Speaker's Feelings & Needs:</label>
                            <Textarea
                              value={fourChoicesResponses[scenario.id]?.speakerFeelings || ''}
                              onChange={(e) => updateFourChoicesResponse(scenario.id, 'speakerFeelings', e.target.value)}
                              placeholder="Are you feeling... because you need...?"
                              className="bg-green-900/20 border-green-700/30 text-white placeholder:text-green-300/40 focus:border-green-600 h-24"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SECTION: Jackal OFNR Practice (Page 87) - Student Input Forms */}
          {currentSection === 5 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Jackal Statements → OFNR Transformation</h2>
                <p className="text-slate-300 text-lg">Practice transforming judgmental statements using Honesty and Empathy OFNR</p>
                <p className="text-slate-400 text-sm mt-2">Page 87 • Use the example below as your guide, then practice on the other statements</p>
              </div>

              {/* OFNR Color Legend */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-6 mb-8">
                <h3 className="text-white font-bold text-lg mb-4 text-center">🎨 OFNR Color Code System</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3 bg-blue-900/40 border border-blue-500/50 rounded-lg p-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">O</div>
                    <div>
                      <p className="text-blue-300 font-semibold text-sm">Observation</p>
                      <p className="text-blue-200/70 text-xs">What you see/hear</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-red-900/40 border border-red-500/50 rounded-lg p-3">
                    <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white font-bold">F</div>
                    <div>
                      <p className="text-red-300 font-semibold text-sm">Feelings</p>
                      <p className="text-red-200/70 text-xs">What's alive in you</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-green-900/40 border border-green-500/50 rounded-lg p-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">N</div>
                    <div>
                      <p className="text-green-300 font-semibold text-sm">Needs</p>
                      <p className="text-green-200/70 text-xs">Universal values</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-yellow-900/40 border border-yellow-500/50 rounded-lg p-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold">R</div>
                    <div>
                      <p className="text-yellow-300 font-semibold text-sm">Request</p>
                      <p className="text-yellow-200/70 text-xs">Actionable ask</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-center">
                  <div className="bg-gradient-to-r from-red-900/30 via-slate-800/50 to-green-900/30 border border-slate-600/50 rounded-lg px-6 py-3 flex items-center gap-4">
                    <span className="text-red-400 font-semibold">Feelings</span>
                    <span className="text-slate-400">↔</span>
                    <span className="text-green-400 font-semibold">Needs</span>
                    <span className="text-slate-300 text-sm ml-2">(Always connected!)</span>
                  </div>
                </div>
              </div>

              {/* Feelings & Needs Helper Button */}
              <div className="flex justify-center mb-6">
                <Button
                  onClick={() => setIsHelperOpen(true)}
                  className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white px-6 py-3"
                >
                  <FeelIcon size={20} className="mr-2" />
                  Open Feelings & Needs Helper
                </Button>
              </div>

              {/* EXAMPLE: Mom always favors you - Complete demonstration */}
              <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 border-2 border-amber-600/50 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-amber-500 text-white text-sm font-bold px-3 py-1 rounded-full">EXAMPLE</span>
                  <h3 className="text-xl font-bold text-amber-200">Jackal Statement: "Mom always favors you."</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mt-4">
                  {/* Honesty OFNR Example */}
                  <div className="bg-green-900/30 border border-green-700/40 rounded-xl p-4">
                    <h4 className="text-green-300 font-bold mb-3 flex items-center gap-2">
                      <span className="text-lg">🦒</span> Honesty OFNR
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">O</span>
                        <p className="text-blue-300">Hearing you say "Mom always favors you"</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">F</span>
                        <p className="text-red-300">I feel frustrated, hurt, confused</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">N</span>
                        <p className="text-green-300">because I need understanding, fairness, to be seen</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-yellow-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">R</span>
                        <p className="text-yellow-300">Would you be willing to sit down with me and Mom to talk about this?</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Empathy OFNR Example */}
                  <div className="bg-blue-900/30 border border-blue-700/40 rounded-xl p-4">
                    <h4 className="text-blue-300 font-bold mb-3 flex items-center gap-2">
                      <span className="text-lg">💙</span> Empathy OFNR
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">O</span>
                        <p className="text-blue-300">When you say "Mom always favors you"</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">F</span>
                        <p className="text-red-300">Are you feeling hurt, sad, longing?</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">N</span>
                        <p className="text-green-300">because you need consideration, closeness, equality?</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="bg-yellow-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">R</span>
                        <p className="text-yellow-300">Is that what's going on for you?</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Student Practice Statements with Input Forms */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white text-center">📝 Your Turn: Practice OFNR Transformations</h3>
                
                {[
                  "Your life is such a mess.",
                  "You are so self centered.",
                  "I can't count on you for anything.",
                  "Stop bragging so much.",
                  "Why do you always talk to your friends about our relationship?",
                  "Do you care about anything besides money?",
                  "You're going on vacation again without me?",
                  "You're such a slob; you always make a mess.",
                  "Stop whining; grow up.",
                  "Can't you just listen to me for once?",
                  "You don't understand anything about me.",
                  "You're always bossing me around.",
                  "You always dominate our conversations.",
                  "Why do I always have to do everything around here?",
                  "You always make my job so hard.",
                  "Why do you text me so much?",
                  "All you ever do is complain.",
                  "Talking to you is like talking to a wall.",
                  "If everybody did as little as you, this company would go out of business.",
                  "With how little you try, you expect to pass this class?",
                  "How come I only hear from you when you need something?",
                  "Stop hovering! Give me some space."
                ].map((statement, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden"
                    data-testid={`jackal-practice-${idx + 2}`}
                  >
                    {/* Statement Header */}
                    <button
                      onClick={() => setExpandedExercise(expandedExercise === `practice-${idx}` ? null : `practice-${idx}`)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="bg-purple-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                          {idx + 2}
                        </span>
                        <p className="text-slate-200 text-left font-medium">"{statement}"</p>
                      </div>
                      {expandedExercise === `practice-${idx}` ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    {/* Expanded Student Input Form */}
                    {expandedExercise === `practice-${idx}` && (
                      <div className="p-4 pt-0 space-y-4 animate-fadeIn">
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Honesty OFNR Input */}
                          <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4">
                            <h4 className="text-green-300 font-bold mb-3">🦒 Your Honesty OFNR</h4>
                            <div className="space-y-3">
                              <div>
                                <label className="flex items-center gap-2 text-sm text-blue-300 mb-1">
                                  <span className="bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">O</span>
                                  Observation
                                </label>
                                <Textarea placeholder="When I hear you say..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                              <div>
                                <label className="flex items-center gap-2 text-sm text-red-300 mb-1">
                                  <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">F</span>
                                  Feelings
                                </label>
                                <Textarea placeholder="I feel..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                              <div>
                                <label className="flex items-center gap-2 text-sm text-green-300 mb-1">
                                  <span className="bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">N</span>
                                  Needs
                                </label>
                                <Textarea placeholder="because I need..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                              <div>
                                <label className="flex items-center gap-2 text-sm text-yellow-300 mb-1">
                                  <span className="bg-yellow-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">R</span>
                                  Request
                                </label>
                                <Textarea placeholder="Would you be willing to..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                            </div>
                          </div>
                          
                          {/* Empathy OFNR Input */}
                          <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4">
                            <h4 className="text-blue-300 font-bold mb-3">💙 Your Empathy OFNR</h4>
                            <div className="space-y-3">
                              <div>
                                <label className="flex items-center gap-2 text-sm text-blue-300 mb-1">
                                  <span className="bg-blue-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">O</span>
                                  Observation
                                </label>
                                <Textarea placeholder="When you say..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                              <div>
                                <label className="flex items-center gap-2 text-sm text-red-300 mb-1">
                                  <span className="bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">F</span>
                                  Feelings (guess)
                                </label>
                                <Textarea placeholder="Are you feeling...?" className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                              <div>
                                <label className="flex items-center gap-2 text-sm text-green-300 mb-1">
                                  <span className="bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">N</span>
                                  Needs (guess)
                                </label>
                                <Textarea placeholder="because you need...?" className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                              <div>
                                <label className="flex items-center gap-2 text-sm text-yellow-300 mb-1">
                                  <span className="bg-yellow-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">R</span>
                                  Connection Request
                                </label>
                                <Textarea placeholder="Is that what's going on for you?" className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[50px]" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Practice Tips */}
              <div className="bg-gradient-to-br from-teal-900/30 to-emerald-900/30 border border-teal-700/40 rounded-xl p-6 mt-6">
                <h4 className="text-teal-300 font-bold text-lg mb-3">💡 Practice Tips</h4>
                <ul className="text-teal-100 space-y-2 text-sm">
                  <li>• <span className="text-blue-300">Blue (O)</span>: Start with what you observed - no judgments</li>
                  <li>• <span className="text-red-300">Red (F)</span> ↔ <span className="text-green-300">Green (N)</span>: Feelings and Needs are always connected!</li>
                  <li>• <span className="text-yellow-300">Gold (R)</span>: End with a doable, specific request</li>
                  <li>• Use the Feelings & Needs Helper above for vocabulary support</li>
                </ul>
              </div>
            </div>
          )}

          {/* SECTION: Street Giraffe Practice (Page 88) - Student Input Forms */}
          {currentSection === 6 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Street Giraffe Practice</h2>
                <p className="text-slate-300 text-lg">Speaking NVC naturally — same consciousness, natural words</p>
                <p className="text-slate-400 text-sm mt-2">Page 88 • Use the example, then practice on other statements</p>
              </div>

              {/* What is Street Giraffe - Two Tips */}
              <div className="bg-green-900/30 border border-green-700/40 rounded-xl p-6 mb-8">
                <h3 className="text-2xl font-bold text-green-300 mb-4">🦒 What is Street Giraffe?</h3>
                <p className="text-green-100 leading-relaxed mb-4">
                  Street giraffe is the practice of sharing honestly what's going on for us in a way that <span className="font-semibold text-green-300">sounds natural to the listener</span>.
                </p>
                <div className="bg-green-900/40 rounded-lg p-5">
                  <p className="text-green-200 font-semibold mb-3">Two Easy Tips:</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="bg-green-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">1</span>
                      <p className="text-green-100">Do <strong className="text-red-400">NOT</strong> say the words <span className="line-through text-red-300">"feelings"</span> or <span className="line-through text-red-300">"needs"</span></p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-green-600 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">2</span>
                      <p className="text-green-100">Put the feelings and needs in <strong className="text-green-300">separate sentences</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feelings & Needs Helper Button */}
              <div className="flex justify-center mb-6">
                <Button
                  onClick={() => setIsHelperOpen(true)}
                  className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white px-6 py-3"
                >
                  <FeelIcon size={20} className="mr-2" />
                  Open Feelings & Needs Helper
                </Button>
              </div>

              {/* EXAMPLE: Mom always favors you */}
              <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 border-2 border-amber-600/50 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-amber-500 text-white text-sm font-bold px-3 py-1 rounded-full">EXAMPLE</span>
                  <h3 className="text-xl font-bold text-amber-200">Jackal Statement: "Mom always favors you."</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mt-4">
                  {/* Street Giraffe Honesty Example */}
                  <div className="bg-green-900/30 border border-green-700/40 rounded-xl p-4">
                    <h4 className="text-green-300 font-bold mb-3">🦒 Street Giraffe Honesty</h4>
                    <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
                      <p className="text-white italic">"Ouch! That is so not how I see it."</p>
                      <p className="text-yellow-400 text-xs">(Wait for response)</p>
                      <p className="text-white italic">"I would love for you to get how this is for me."</p>
                    </div>
                    <div className="text-xs mt-3 border-t border-slate-600/30 pt-2">
                      <p><span className="text-red-400">Implied Feelings:</span> <span className="text-red-300">hurt, surprised</span></p>
                      <p><span className="text-green-400">Implied Needs:</span> <span className="text-green-300">understanding, to be seen</span></p>
                    </div>
                  </div>
                  
                  {/* Street Giraffe Empathy Example */}
                  <div className="bg-blue-900/30 border border-blue-700/40 rounded-xl p-4">
                    <h4 className="text-blue-300 font-bold mb-3">💙 Street Giraffe Empathy</h4>
                    <div className="bg-slate-800/50 rounded-lg p-3 space-y-2">
                      <p className="text-white italic">"Sounds like your relationship is pretty tough these days."</p>
                      <p className="text-yellow-400 text-xs">(Wait for response)</p>
                      <p className="text-white italic">"You really want to be treated fairly, don't you."</p>
                    </div>
                    <div className="text-xs mt-3 border-t border-slate-600/30 pt-2">
                      <p><span className="text-red-400">Guessing Feelings:</span> <span className="text-red-300">hurt, left out</span></p>
                      <p><span className="text-green-400">Guessing Needs:</span> <span className="text-green-300">fairness, inclusion</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Student Practice Statements */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-white text-center">📝 Your Turn: Practice Street Giraffe</h3>
                
                {[
                  "Your life is such a mess.",
                  "You are so self centered.",
                  "I can't count on you for anything.",
                  "Stop bragging so much.",
                  "Why do you always talk to your friends about our relationship?",
                  "Do you care about anything besides money?",
                  "You're going on vacation again without me?",
                  "Stop whining; grow up.",
                  "Talking to you is like talking to a wall."
                ].map((statement, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden"
                    data-testid={`street-practice-${idx + 2}`}
                  >
                    {/* Statement Header */}
                    <button
                      onClick={() => setExpandedExercise(expandedExercise === `street-practice-${idx}` ? null : `street-practice-${idx}`)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="bg-green-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                          {idx + 2}
                        </span>
                        <p className="text-slate-200 text-left font-medium">"{statement}"</p>
                      </div>
                      {expandedExercise === `street-practice-${idx}` ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </button>
                    
                    {/* Expanded Student Input Form */}
                    {expandedExercise === `street-practice-${idx}` && (
                      <div className="p-4 pt-0 space-y-4 animate-fadeIn">
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Street Giraffe Honesty Input */}
                          <div className="bg-green-900/20 border border-green-700/30 rounded-xl p-4">
                            <h4 className="text-green-300 font-bold mb-3">🦒 Your Street Giraffe Honesty</h4>
                            <p className="text-slate-400 text-xs mb-3">Remember: No "feelings" or "needs" words! Natural language only.</p>
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm text-slate-300 mb-1 block">Your initial response:</label>
                                <Textarea placeholder="e.g., Ouch! That is hard to hear..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[60px]" />
                              </div>
                              <div className="text-center text-yellow-400 text-xs">(Wait for response)</div>
                              <div>
                                <label className="text-sm text-slate-300 mb-1 block">Your follow-up:</label>
                                <Textarea placeholder="e.g., I would really like you to understand..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[60px]" />
                              </div>
                              <div className="border-t border-slate-600/30 pt-2">
                                <label className="text-xs text-red-300 mb-1 block">What feelings are implied?</label>
                                <Textarea placeholder="e.g., hurt, defensive" className="bg-slate-900/50 border-slate-600 text-white text-xs min-h-[40px]" />
                                <label className="text-xs text-green-300 mb-1 block mt-2">What needs are implied?</label>
                                <Textarea placeholder="e.g., acceptance, understanding" className="bg-slate-900/50 border-slate-600 text-white text-xs min-h-[40px]" />
                              </div>
                            </div>
                          </div>
                          
                          {/* Street Giraffe Empathy Input */}
                          <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-4">
                            <h4 className="text-blue-300 font-bold mb-3">💙 Your Street Giraffe Empathy</h4>
                            <p className="text-slate-400 text-xs mb-3">Guess their feelings and needs naturally.</p>
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm text-slate-300 mb-1 block">Your initial empathy guess:</label>
                                <Textarea placeholder="e.g., It sounds like you are worried about me..." className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[60px]" />
                              </div>
                              <div className="text-center text-yellow-400 text-xs">(Wait for response)</div>
                              <div>
                                <label className="text-sm text-slate-300 mb-1 block">Your follow-up guess:</label>
                                <Textarea placeholder='e.g., "Are you wanting things to be easier for me?"' className="bg-slate-900/50 border-slate-600 text-white text-sm min-h-[60px]" />
                              </div>
                              <div className="border-t border-slate-600/30 pt-2">
                                <label className="text-xs text-red-300 mb-1 block">What feelings are you guessing?</label>
                                <Textarea placeholder="e.g., concerned, worried" className="bg-slate-900/50 border-slate-600 text-white text-xs min-h-[40px]" />
                                <label className="text-xs text-green-300 mb-1 block mt-2">What needs are you guessing?</label>
                                <Textarea placeholder="e.g., wellbeing, ease" className="bg-slate-900/50 border-slate-600 text-white text-xs min-h-[40px]" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Classical vs Street Giraffe Comparison */}
              <div className="bg-gradient-to-br from-purple-900/30 to-fuchsia-900/30 border border-purple-700/40 rounded-xl p-6 mt-6">
                <h4 className="text-purple-300 font-bold text-lg mb-3">🎯 Classical vs Street Giraffe</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-600/30">
                    <p className="text-purple-300 font-semibold mb-2">Classical Giraffe:</p>
                    <p className="text-purple-100 text-sm italic">"When I hear you say you won't be home until late, <span className="text-red-300">I feel disappointed</span> because <span className="text-green-300">I need connection</span>. Would you be willing to have dinner together tomorrow night?"</p>
                  </div>
                  <div className="bg-green-900/30 rounded-lg p-4 border border-green-600/30">
                    <p className="text-green-300 font-semibold mb-2">Street Giraffe:</p>
                    <p className="text-green-100 text-sm italic">"Oh darn, <span className="text-red-300">I'm disappointed</span> to hear that. <span className="text-green-300">I was hoping for some closeness tonight.</span> Are you interested in having dinner tomorrow night?"</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mt-4 text-center italic">
                  The point isn't to use specific words — it's to incorporate the <span className="text-teal-300 font-semibold">consciousness</span> into how you think and communicate.
                </p>
              </div>
            </div>
          )}

          {/* SECTION: Using OFNR in Honesty and Empathy (Page 89) */}
          {currentSection === 7 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Using OFNR in Honesty and Empathy</h2>
                <p className="text-slate-300 text-lg">A step-by-step guide to creating connection</p>
                <p className="text-slate-400 text-sm mt-2">Page 89</p>
              </div>

              {/* Introduction */}
              <div className="bg-gradient-to-br from-teal-900/30 to-emerald-900/30 border border-teal-700/40 rounded-xl p-6">
                <p className="text-teal-100 text-lg leading-relaxed">
                  OFNR can be used in two powerful ways: <span className="text-green-300 font-semibold">Honesty</span> (expressing what's alive in us) and <span className="text-blue-300 font-semibold">Empathy</span> (guessing what's alive in others). Both paths lead to connection.
                </p>
              </div>

              {/* OFNR Color Legend */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-white font-bold text-lg mb-4 text-center">🎨 Remember the Color Code</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-xl mx-auto mb-2">O</div>
                    <p className="text-blue-300 font-semibold text-sm">Observation</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-xl mx-auto mb-2">F</div>
                    <p className="text-red-300 font-semibold text-sm">Feelings</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xl mx-auto mb-2">N</div>
                    <p className="text-green-300 font-semibold text-sm">Needs</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-yellow-500 flex items-center justify-center text-white font-bold text-xl mx-auto mb-2">R</div>
                    <p className="text-yellow-300 font-semibold text-sm">Request</p>
                  </div>
                </div>
              </div>

              {/* Step 1: Expressing Honesty */}
              <div className="bg-gradient-to-br from-green-900/40 to-teal-900/30 border-2 border-green-600/50 rounded-2xl overflow-hidden">
                <div className="bg-green-800/50 p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">1</div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-300">Express Honesty</h3>
                    <p className="text-green-200/70">Share what's alive in YOU using OFNR</p>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-slate-200 leading-relaxed">
                    When you want to express yourself authentically, follow the OFNR structure to share your experience without blame or judgment.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 bg-blue-900/30 border border-blue-600/40 rounded-lg p-3">
                        <span className="bg-blue-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">O</span>
                        <div>
                          <p className="text-blue-300 font-semibold">Observation</p>
                          <p className="text-slate-300 text-sm">"When I see/hear/notice..."</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-red-900/30 border border-red-600/40 rounded-lg p-3">
                        <span className="bg-red-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">F</span>
                        <div>
                          <p className="text-red-300 font-semibold">Feelings</p>
                          <p className="text-slate-300 text-sm">"I feel..."</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 bg-green-900/30 border border-green-600/40 rounded-lg p-3">
                        <span className="bg-green-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">N</span>
                        <div>
                          <p className="text-green-300 font-semibold">Needs</p>
                          <p className="text-slate-300 text-sm">"Because I need/value..."</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-yellow-900/30 border border-yellow-600/40 rounded-lg p-3">
                        <span className="bg-yellow-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">R</span>
                        <div>
                          <p className="text-yellow-300 font-semibold">Request</p>
                          <p className="text-slate-300 text-sm">"Would you be willing to..."</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
                    <p className="text-slate-400 text-sm mb-2">Example:</p>
                    <p className="text-white">
                      "<span className="text-blue-300">When I came home and saw dishes in the sink</span>, 
                      <span className="text-red-300"> I felt frustrated</span> 
                      <span className="text-green-300"> because I really value order and ease when I get home</span>. 
                      <span className="text-yellow-300">Would you be willing to wash your dishes before I get back tomorrow?</span>"
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2: Offering Empathy */}
              <div className="bg-gradient-to-br from-blue-900/40 to-indigo-900/30 border-2 border-blue-600/50 rounded-2xl overflow-hidden">
                <div className="bg-blue-800/50 p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">2</div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-300">Offer Empathy</h3>
                    <p className="text-blue-200/70">Guess what's alive in THEM using OFNR</p>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-slate-200 leading-relaxed">
                    When someone speaks to you, especially with jackal energy, use OFNR to guess their feelings and needs. This creates connection.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 bg-blue-900/30 border border-blue-600/40 rounded-lg p-3">
                        <span className="bg-blue-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">O</span>
                        <div>
                          <p className="text-blue-300 font-semibold">Observation</p>
                          <p className="text-slate-300 text-sm">"When you say/do..."</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-red-900/30 border border-red-600/40 rounded-lg p-3">
                        <span className="bg-red-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">F</span>
                        <div>
                          <p className="text-red-300 font-semibold">Feelings (guess)</p>
                          <p className="text-slate-300 text-sm">"Are you feeling...?"</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 bg-green-900/30 border border-green-600/40 rounded-lg p-3">
                        <span className="bg-green-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">N</span>
                        <div>
                          <p className="text-green-300 font-semibold">Needs (guess)</p>
                          <p className="text-slate-300 text-sm">"Because you need...?"</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-yellow-900/30 border border-yellow-600/40 rounded-lg p-3">
                        <span className="bg-yellow-500 text-white text-sm font-bold w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0">R</span>
                        <div>
                          <p className="text-yellow-300 font-semibold">Connection Request</p>
                          <p className="text-slate-300 text-sm">"Is that what's going on?"</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-800/50 rounded-lg p-4 mt-4">
                    <p className="text-slate-400 text-sm mb-2">Example (responding to "You never listen to me!"):</p>
                    <p className="text-white">
                      "<span className="text-blue-300">When you say I never listen</span>, 
                      <span className="text-red-300"> are you feeling frustrated and maybe a bit lonely</span> 
                      <span className="text-green-300"> because you really need to be heard and understood</span>? 
                      <span className="text-yellow-300">Is that what's going on for you?</span>"
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3: The Dance */}
              <div className="bg-gradient-to-br from-purple-900/40 to-fuchsia-900/30 border-2 border-purple-600/50 rounded-2xl overflow-hidden">
                <div className="bg-purple-800/50 p-4 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-2xl shadow-lg">3</div>
                  <div>
                    <h3 className="text-2xl font-bold text-purple-300">The Dance of Connection</h3>
                    <p className="text-purple-200/70">Alternating between Honesty and Empathy</p>
                  </div>
                </div>
                <div className="p-6 space-y-4">
                  <p className="text-slate-200 leading-relaxed">
                    Real conversations flow between honesty and empathy. Sometimes you share, sometimes you listen and guess. The magic happens in the dance.
                  </p>
                  
                  <div className="bg-slate-800/50 rounded-lg p-5">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">THEM</span>
                        <p className="text-slate-300 italic">"You're always late! You don't care about my time at all."</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">YOU</span>
                        <p className="text-blue-200"><span className="font-semibold">(Empathy)</span> "Sounds like you're really frustrated. You value punctuality and want to feel like your time matters to me?"</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">THEM</span>
                        <p className="text-slate-300 italic">"Yes! I feel like I'm always waiting for you."</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">YOU</span>
                        <p className="text-green-200"><span className="font-semibold">(Honesty)</span> "I hear that, and I feel sad hearing it. I really do care about you. I've been struggling with time management. Would you be willing to text me 30 minutes before we meet?"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Insight */}
              <div className="bg-gradient-to-r from-red-900/30 via-slate-800/40 to-green-900/30 border border-slate-600/50 rounded-xl p-6 text-center">
                <h4 className="text-2xl font-bold text-white mb-3">The Heart of NVC</h4>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-red-500/30 border-2 border-red-500 flex items-center justify-center mx-auto mb-2">
                      <span className="text-3xl">❤️</span>
                    </div>
                    <p className="text-red-300 font-semibold">Feelings</p>
                  </div>
                  <div className="text-3xl text-slate-400">↔</div>
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-green-500/30 border-2 border-green-500 flex items-center justify-center mx-auto mb-2">
                      <span className="text-3xl">🌱</span>
                    </div>
                    <p className="text-green-300 font-semibold">Needs</p>
                  </div>
                </div>
                <p className="text-slate-300 mt-4 max-w-xl mx-auto">
                  Feelings are messengers that tell us which needs are being met or unmet. When we understand this connection, compassion flows naturally.
                </p>
              </div>

              {/* Feelings & Needs Helper */}
              <div className="flex justify-center">
                <Button
                  onClick={() => setIsHelperOpen(true)}
                  className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white px-8 py-4 text-lg"
                >
                  <FeelIcon size={24} className="mr-2" />
                  Open Feelings & Needs Helper
                </Button>
              </div>
            </div>
          )}

          {/* SECTION: Four D's of Disconnection with Interactive Exercises */}
          {currentSection === 8 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center mb-8">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">The Four D's of Disconnection</h2>
                <p className="text-slate-300 text-lg">Each of these is a tragic expression of an unmet need</p>
                <p className="text-slate-400 text-sm mt-2">Page 90</p>
              </div>

              {/* Four D's Cards with Write-in Examples */}
              <div className="grid md:grid-cols-2 gap-6">
                {fourDs.map((d, idx) => (
                  <div key={d.name} className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-red-700/50 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="bg-red-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <h3 className="text-2xl font-bold text-red-300">{d.name}</h3>
                    </div>
                    <p className="text-slate-300 mb-3"><strong className="text-slate-200">Meaning:</strong> {d.meaning}</p>
                    <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3 mb-3">
                      <p className="text-red-200 text-sm"><strong>Example:</strong> "{d.example}"</p>
                    </div>
                    <p className="text-slate-400 text-sm mb-4"><strong>Form:</strong> {d.form}</p>
                    
                    {/* Exercise 1: Write-in box */}
                    <div className="border-t border-slate-600/50 pt-4 mt-4">
                      <p className="text-slate-400 text-sm mb-2">Exercise 1: Write an example you've heard recently:</p>
                      <Textarea
                        placeholder={`Write a ${d.name.toLowerCase()} statement you've heard...`}
                        className="bg-slate-900/50 border-slate-600 text-slate-200 min-h-[80px] placeholder:text-slate-500"
                        data-testid={`fourds-example-${d.name.toLowerCase()}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-green-900/20 to-teal-900/20 border border-green-700/30 rounded-xl p-6">
                <h4 className="text-green-300 font-bold text-lg mb-2">Remember:</h4>
                <p className="text-slate-200">
                  Behind every "D" is an unmet need. When we hear these patterns, we can ask ourselves: 
                  <em className="text-green-300"> "What need might this person be trying to express?"</em>
                </p>
              </div>

              {/* EXERCISE 2: Match Statements to Response Patterns - Multi-Select */}
              <div className="mt-12 bg-gradient-to-br from-blue-900/30 to-purple-900/30 border border-blue-700/40 rounded-2xl p-8">
                <div className="mb-6">
                  <span className="bg-blue-600 text-white text-sm font-bold px-3 py-1 rounded-full">Exercise 2</span>
                  <h3 className="text-2xl font-bold text-white mt-3">Match the Response Pattern</h3>
                  <p className="text-slate-300 mt-2">For each response to: <span className="text-amber-300 font-semibold">"Maybe if you told me what time you wanted me home, we wouldn't be having this problem."</span></p>
                  <p className="text-slate-400 text-sm mt-2">Select ALL patterns that apply (some have multiple correct answers)</p>
                </div>

                {/* Matching Items with Multi-Select */}
                <div className="space-y-4">
                  {exercise2Data.map((item) => (
                    <div key={item.id} className="bg-slate-800/60 border border-slate-600/50 rounded-lg p-4">
                      <div className="flex items-start gap-4 mb-3">
                        <span className="bg-blue-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                          {item.id}
                        </span>
                        <p className="text-slate-200 flex-1 italic">"{item.text}"</p>
                      </div>
                      <div className="ml-12 flex flex-wrap gap-2">
                        {patternOptions.map((pattern) => (
                          <button
                            key={pattern.id}
                            onClick={() => toggleExercise2Selection(item.id, pattern.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              (exercise2Selections[item.id] || []).includes(pattern.id)
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {pattern.id}. {pattern.name}
                          </button>
                        ))}
                      </div>
                      {showExercise2Answers && (
                        <div className="ml-12 mt-3 px-3 py-2 rounded bg-green-600/20 border border-green-500/30">
                          <p className="text-green-300 text-sm">
                            <span className="font-bold">✓ Correct Answer:</span> {item.answerNames}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Reveal Answers Button */}
                <div className="flex justify-center mt-6">
                  <Button
                    onClick={() => setShowExercise2Answers(!showExercise2Answers)}
                    className={`px-8 py-3 ${showExercise2Answers ? 'bg-green-600 hover:bg-green-500' : 'bg-blue-600 hover:bg-blue-500'}`}
                  >
                    {showExercise2Answers ? '✓ Answers Revealed' : 'Reveal Answers'}
                  </Button>
                </div>
              </div>

              {/* EXERCISE 3: Identify Response Pattern in Dialogues - Multi-Select */}
              <div className="mt-8 bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-700/40 rounded-2xl p-8">
                <div className="mb-6">
                  <span className="bg-purple-600 text-white text-sm font-bold px-3 py-1 rounded-full">Exercise 3</span>
                  <h3 className="text-2xl font-bold text-white mt-3">Identify the Response Pattern</h3>
                  <p className="text-slate-300 mt-2">From each dialogue, identify which response pattern(s) Person B is using.</p>
                  <p className="text-slate-400 text-sm mt-1">Select ALL patterns that apply</p>
                </div>

                <div className="space-y-4">
                  {exercise3DialogueData.map((item) => (
                    <div key={item.id} className="bg-slate-800/60 border border-slate-600/50 rounded-lg p-4">
                      <div className="flex items-start gap-2 mb-3">
                        <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">{item.id}</span>
                        <div className="flex-1">
                          <div className="mb-2">
                            <span className="text-red-400 font-semibold">A:</span>
                            <span className="text-slate-300 ml-2">"{item.a}"</span>
                          </div>
                          <div>
                            <span className="text-blue-400 font-semibold">B:</span>
                            <span className="text-white ml-2 font-medium">"{item.b}"</span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-8 flex flex-wrap gap-2">
                        {exercise3PatternOptions.map((pattern) => (
                          <button
                            key={pattern.id}
                            onClick={() => toggleExercise3Selection(item.id, pattern.id)}
                            className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                              (exercise3Selections[item.id] || []).includes(pattern.id)
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            }`}
                          >
                            {pattern.name}
                          </button>
                        ))}
                      </div>
                      {showExercise3AnswersNew && (
                        <div className="ml-8 mt-3 px-3 py-2 rounded bg-green-600/20 border border-green-500/30">
                          <p className="text-green-300 text-sm">
                            <span className="font-bold">✓ Correct Answer:</span> {item.answerNames}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Reveal Answers Button */}
                <div className="flex justify-center mt-6">
                  <Button
                    onClick={() => setShowExercise3AnswersNew(!showExercise3AnswersNew)}
                    className={`px-8 py-3 ${showExercise3AnswersNew ? 'bg-green-600 hover:bg-green-500' : 'bg-purple-600 hover:bg-purple-500'}`}
                  >
                    {showExercise3AnswersNew ? '✓ Answers Revealed' : 'Reveal Answers'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* SECTION: Homework */}
          {currentSection === 9 && (
            <div className="space-y-8 animate-fadeIn">
              <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-8">
                <h2 className="text-3xl font-bold text-white mb-6">Chapter 1 Homework</h2>
                
                <div className="space-y-4">
                  {[
                    { num: 1, text: 'Complete exercises 2 and 3 above' },
                    { num: 2, text: 'Read pages 63-70: "Nonviolent Communication Primer"', link: '/workbook/nvc-primer' },
                    { num: 3, text: 'Read page 71: "Compassionate Communication Suggestions"', link: '/workbook/compassionate-suggestions' },
                    { num: 4, text: 'Read page 72: "Compassionate Communication"' },
                    { num: 5, text: 'Read pages 73-74: "Lost Love and the Drive Towards Wholeness"', link: '/workbook/lost-love' },
                    { num: 6, text: 'Read pages 75-76: "When Your Past Shows Up in the Present"', link: '/workbook/past-in-present' },
                    { num: 7, text: 'Watch Part 1 of NVC basics video (00:00-47:16)' },
                    { num: 8, text: 'Read Chapters 1, 2, 3 in "Nonviolent Communication: A Language of Life"' },
                  ].map((item) => (
                    <div key={item.num} className="flex items-start gap-4 bg-slate-700/30 rounded-lg p-4">
                      <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {item.num}
                      </span>
                      <div className="flex-1">
                        <p className="text-white">{item.text}</p>
                        {item.link && (
                          <Button 
                            onClick={() => navigate(item.link)} 
                            variant="link" 
                            className="text-teal-400 hover:text-teal-300 p-0 h-auto mt-1"
                          >
                            → Go to page
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={() => navigate('/workbook/chapter-1-homework')} 
                  className="w-full mt-6 bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700"
                  size="lg"
                >
                  Go to Interactive Homework Tracker →
                </Button>
              </div>

              {/* Beautiful camp image */}
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={CHAPTER_IMAGES.camp} 
                  alt="Camp gathering" 
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-12 pt-8 border-t border-slate-700/50">
            <Button
              onClick={() => setCurrentSection(prev => Math.max(prev - 1, 0))}
              disabled={currentSection === 0}
              variant="outline"
              size="lg"
              className="border-slate-600 text-white hover:bg-slate-800 disabled:opacity-30"
            >
              <ArrowLeftIcon size={20} className="mr-2" />
              Previous
            </Button>

            <div className="text-center">
              <p className="text-slate-400 text-sm">
                Section {currentSection + 1} of {sections.length}
              </p>
              <p className="text-white font-semibold">{sections[currentSection].title}</p>
            </div>

            <Button
              onClick={() => setCurrentSection(prev => Math.min(prev + 1, sections.length - 1))}
              disabled={currentSection === sections.length - 1}
              size="lg"
              className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700 disabled:opacity-30"
            >
              Next
              <ArrowRightIcon size={20} className="ml-2" />
            </Button>
          </div>

          {/* Practice Scenario & Contact Form */}
          <EbookPageFooter 
            pageContext="Chapter 1: Communication that Blocks Compassion"
            pageNumber={79}
            showScenario={true}
            showContactForm={true}
          />
        </div>
      </div>

      {/* Presentation Mode Hint */}
      {isPresentationMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-800/90 text-slate-300 px-4 py-2 rounded-full text-sm backdrop-blur-sm">
          Use ← → arrows or Space to navigate • ESC to exit
        </div>
      )}

      {/* Feelings & Needs Helper Panel/Modal */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
        mode="panel"
      />

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
